import { motion } from 'framer-motion';
import { Link } from 'wouter';
import { Plus, Users, ChevronDown, ChevronUp, MapPin, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAdminEvents, useCreateEvent, useDeleteEvent, useAdminNeighborhoods } from '../api';
import { formatDateToFull, formatDateToShort, formatTimeToDisplay } from '@/utils/dateUtils';
import { EVENT_TYPE_CONFIG } from '@/utils/eventTypeUtils';
import { NextEventsTimer } from '../components';
import dayjs from 'dayjs';
import { Loader } from '@/components/Loader';

export default function AdminEvents() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [selectedNeighborhoodIds, setSelectedNeighborhoodIds] = useState<string[]>([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  
  const { register, handleSubmit, reset } = useForm();

  const { data: eventsData, isLoading } = useAdminEvents();
  const { data: neighborhoodsData } = useAdminNeighborhoods();

  const createEvent = useCreateEvent();
  const deleteEvent = useDeleteEvent();

  const allEvents = eventsData?.data || [];

  // Separate events into upcoming and expired
  const cutoff = dayjs().subtract(2, "day").format('YYYY-MM-DD');

  const upcomingEvents = allEvents.filter((event: any) => event.date >= cutoff);
  const expiredEvents = allEvents.filter((event: any) => event.date < cutoff).sort((a: any, b: any) => b.date > a.date ? 1 : -1);

  const allNeighborhoods = neighborhoodsData?.data?.neighborhoods || [];
  const allCities = neighborhoodsData?.data?.cities || [];

  const toggleNeighborhood = (id: string) => {
    setSelectedNeighborhoodIds(prev =>
      prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]
    );
  };

  const onSubmit = (data: any) => {
    createEvent.mutate(
      {
        ...data,
        neighborhoodIds: selectedNeighborhoodIds.length > 0 ? selectedNeighborhoodIds : null,
      },
      {
        onSuccess: () => {
          setShowCreateForm(false);
          setSelectedNeighborhoodIds([]);
          reset();
        },
      }
    );
  };

  const renderEventCard = (event: any, index: number, isExpired: boolean = false) => {
    return (
      <motion.div
        key={event.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className={`card ${isExpired ? 'opacity-60' : ''}`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-xl font-bold">
                {formatDateToFull(event.date)}
              </h3>
            </div>
            <div className="flex items-center space-x-4 text-sm text-dark-muted mb-2">
              <span>{EVENT_TYPE_CONFIG[event.type || 'dinner']?.emoji || '🍽️'} {EVENT_TYPE_CONFIG[event.type || 'dinner']?.label || 'Dinner'}</span>
              <span>📅 {formatDateToShort(event.date)}</span>
              <span>⏰ {formatTimeToDisplay(event.time)} </span>
            </div>
            <div className="flex items-center space-x-4 text-sm">

              <span className="text-green-400">
                {event.activeBookingsCount} Active
              </span>
              {event.bookingsCount - event.activeBookingsCount - event.pendingBookingsCount - (event.cantBeSeatedBookingsCount ?? 0) > 0 && (
                <span className="text-dark-muted">
                  {event.bookingsCount - event.activeBookingsCount - event.pendingBookingsCount - (event.cantBeSeatedBookingsCount ?? 0)} Cancelled
                </span>
              )}
              {(event.cantBeSeatedBookingsCount ?? 0) > 0 && (
                <span className="text-amber-400">
                  {event.cantBeSeatedBookingsCount} Can't be seated
                </span>
              )}
              <span className="text-primary-400 font-semibold">
                👥 {event.bookingsCount} Total
              </span>
              {event.pendingBookingsCount > 0 && (
                <span className="text-yellow-400">
                  {event.pendingBookingsCount} Pending
                </span>
              )}
            </div>
            {event.neighborhoodIds && event.neighborhoodIds.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-amber-400 mt-1">
                <MapPin className="w-3 h-3" />
                <span>
                  Restricted to {event.neighborhoodIds.length} {event.neighborhoodIds.length === 1 ? 'neighborhood' : 'neighborhoods'}:
                  {' '}{event.neighborhoodIds.map((nId: string) => {
                    const n = allNeighborhoods.find((nb: any) => nb.id === nId);
                    return n?.name || nId;
                  }).join(', ')}
                </span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Link href={`/matching/${event.id}`} className="btn btn-ghost">
              <Users className="w-4 h-4 mr-2 inline-block" />
              View Details
            </Link>
            <button
              onClick={() => setConfirmDeleteId(event.id)}
              className="btn btn-ghost text-dark-muted hover:text-red-400 transition-colors"
              title="Delete event"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  return (
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8 flex-col gap-2 md:flex-row"
        >
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-2">Event Management</h1>
            <p className="text-dark-muted">Create and manage dinner events</p>
          </div>
          <div className="flex-1"></div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn-primary w-auto"
          >
            <Plus className="w-5 h-5 mr-2 inline-block" />
            Create Event
          </button>
        </motion.div>

        {/* Next Events Countdown */}
        <NextEventsTimer />

        {/* Create Event Form */}
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card mb-8"
          >
            <h2 className="text-2xl font-bold mb-6">Create New Event</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Date</label>
                  <input
                    {...register('date', { required: true })}
                    type="date"
                    className="input"
                  />
                </div>

                <div>
                  <label className="label">Time</label>
                  <input
                    {...register('time', { required: true })}
                    type="time"
                    className="input"
                    defaultValue="19:00"
                  />
                </div>

                <div>
                  <label className="label">Type</label>
                  <select
                    {...register('type')}
                    className="input"
                    defaultValue="dinner"
                  >
                    {["dinner", "brunch", "drinks", "coffee"].map((value) => (
                      <option key={value} value={value}>
                        {EVENT_TYPE_CONFIG[value].emoji} {EVENT_TYPE_CONFIG[value].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Restrict to Neighborhoods (optional)</label>
                <p className="text-sm text-dark-muted mb-2">
                  Leave empty to allow all neighborhoods. Select specific neighborhoods to restrict this event.
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-dark-border rounded-lg p-3">
                  {allCities.map((city: any) => {
                    const cityNeighborhoods = allNeighborhoods.filter((n: any) => n.cityId === city.id && n.isActive);
                    if (cityNeighborhoods.length === 0) return null;
                    return (
                      <div key={city.id}>
                        <div className="text-xs font-semibold text-dark-muted uppercase tracking-wider mb-1">{city.name}</div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {cityNeighborhoods.map((n: any) => (
                            <button
                              key={n.id}
                              type="button"
                              onClick={() => toggleNeighborhood(n.id)}
                              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                selectedNeighborhoodIds.includes(n.id)
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-dark-card text-dark-muted hover:bg-dark-border'
                              }`}
                            >
                              {n.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedNeighborhoodIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedNeighborhoodIds([])}
                    className="text-sm text-primary-400 hover:text-primary-300 mt-1"
                  >
                    Clear selection (allow all)
                  </button>
                )}
              </div>

              <div className="flex gap-4">
                <button type="submit" className="btn btn-primary" disabled={createEvent.isPending}>
                  {createEvent.isPending ? 'Creating...' : 'Create Event'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Upcoming Events List */}
        <div className="space-y-4">
          {upcomingEvents.map((event: any, index: number) => renderEventCard(event, index, false))}

          {upcomingEvents.length === 0 && expiredEvents.length === 0 && (
            <div className="card text-center py-16">
              <Plus className="w-16 h-16 text-dark-muted mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No events yet</h3>
              <p className="text-dark-muted mb-6">Create your first dinner event to get started</p>
              <button onClick={() => setShowCreateForm(true)} className="btn btn-primary">
                Create Event
              </button>
            </div>
          )}

          {upcomingEvents.length === 0 && expiredEvents.length > 0 && (
            <div className="card text-center py-16">
              <Plus className="w-16 h-16 text-dark-muted mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No upcoming events</h3>
              <p className="text-dark-muted mb-6">Create a new dinner event to get started</p>
              <button onClick={() => setShowCreateForm(true)} className="btn btn-primary">
                Create Event
              </button>
            </div>
          )}
        </div>

        {/* Expired Events Section */}
        {expiredEvents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-12"
          >
            <button
              onClick={() => setShowExpired(!showExpired)}
              className="flex items-center space-x-2 text-dark-muted hover:text-white transition-colors mb-6"
            >
              {showExpired ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
              <span className="text-lg font-semibold">
                Past Events ({expiredEvents.length})
              </span>
            </button>

            {showExpired && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
              >
                {expiredEvents.map((event: any, index: number) => renderEventCard(event, index, true))}
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Delete Confirmation Dialog */}
        {confirmDeleteId && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDeleteId(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-2">Delete Event</h3>
              <p className="text-dark-muted mb-1">
                Are you sure you want to delete this event?
              </p>
              <p className="text-sm text-dark-muted mb-6">
                {(() => {
                  const event = allEvents.find((e: any) => e.id === confirmDeleteId);
                  if (!event) return confirmDeleteId;
                  return `${formatDateToFull(event.date)} at ${formatTimeToDisplay(event.time)}`;
                })()}
                {' — '}
                This will also delete all associated bookings and table assignments.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className="btn btn-ghost"
                  disabled={deleteEvent.isPending}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    deleteEvent.mutate(confirmDeleteId, {
                      onSuccess: () => setConfirmDeleteId(null),
                    });
                  }}
                  disabled={deleteEvent.isPending}
                  className="btn bg-red-600 hover:bg-red-500 text-white"
                >
                  {deleteEvent.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
  );
}

